import { ProCard } from '@ant-design/pro-components'
import { sync } from '@app/icare/client/sync'
import { AcademicTitleEnum } from '@app/icare/db/schema/common'
import {
  type Doctor,
  type DoctorIdType,
  DoctorSchema,
  DoctorSpecialityEnum,
} from '@app/icare/entities/doctor/schema/Doctor'
import { EntityDrawerShell } from '@app/icare/form/EntityDrawerShell'
import { AddressFieldGroup } from '@app/icare/form/group/AddressFieldGroup'
import { useEntityDrawer } from '@app/icare/hooks/useEntityDrawer'
import { t } from '@lingui/core/macro'
import { SectionFlow } from '@pkg/form/index'
import type { SectionDef } from '@pkg/form/useSectionFlow'
import { zodDefaultInstance } from '@pkg/shared/form'
import { createTypedModal, type EditModalProps, type EditModalResult } from '@pkg/ui/modal'
import { Flex } from 'antd'

const sections = [
  { name: 'basic', title: t`Allgemein` },
  { name: 'misc', title: t`Sonstiges` },
] as const satisfies readonly SectionDef[]

type DoctorSection = (typeof sections)[number]['name']

type DoctorEditDrawerProps =
  | (EditModalProps<Doctor, DoctorSection, DoctorIdType> & { showGlobalImpactWarning?: boolean })
  | undefined

type DoctorEditDrawerShow = (props?: DoctorEditDrawerProps) => Promise<EditModalResult<Doctor>>

export const DoctorEditDrawer = createTypedModal<DoctorEditDrawerProps, EditModalResult<Doctor>, DoctorEditDrawerShow>(
  props => {
    const drawer = useEntityDrawer({
      schema: DoctorSchema,
      entityId: props?.id,
      sections,
      section: props?.section,
      formDefaults: {
        ...zodDefaultInstance(DoctorSchema),
        ...props?.defaults,
      },
      onPersist: async value => {
        await sync.mutate(value)
      },
    })

    const { form } = drawer

    return (
      <EntityDrawerShell drawer={drawer} showWarnings={props?.showGlobalImpactWarning}>
        <SectionFlow.Sections
          items={[
            {
              name: 'basic',
              title: t`Allgemein`,
              children: (
                <ProCard>
                  <Flex vertical gap="small">
                    <form.AppField
                      name="speciality"
                      children={field => (
                        <field.Select
                          className="col-span-12"
                          label="Fachrichtung"
                          options={DoctorSpecialityEnum.$.options}
                          disabled={props?.showGlobalImpactWarning}
                        />
                      )}
                    />

                    <form.AppField
                      name="title"
                      children={field => (
                        <field.Select className="col-span-2" label="Titel" options={AcademicTitleEnum.$.options} />
                      )}
                    />

                    <form.AppField
                      name="name"
                      children={field => (
                        <field.Text label="Name" className="col-span-7" disabled={props?.showGlobalImpactWarning} />
                      )}
                    />

                    <form.AppField
                      name="nameComplement"
                      children={field => <field.Text label="Namenszusatz" className="col-span-3" />}
                    />

                    <AddressFieldGroup form={form} fields="address" />

                    <form.AppField
                      name="contact"
                      children={field => <field.ContactGroup hidePreferredCommunication />}
                    />
                  </Flex>
                </ProCard>
              ),
            },
            {
              name: 'misc',
              title: t`Sonstiges`,
              children: (
                <ProCard>
                  <Flex vertical gap="small">
                    <form.AppField
                      name="comment"
                      children={field => <field.TextArea label={t`Bemerkungen`} className="grow" />}
                    />
                  </Flex>
                </ProCard>
              ),
            },
          ]}
        />
      </EntityDrawerShell>
    )
  },
)
